import { useEffect, useRef, useState } from "react";
import {
  X,
  Check,
  ArrowUpRight,
} from "lucide-react";
import useGClubStatus from "@/hooks/useGClubStatus";
import useCountdown from "@/hooks/useCountdown";

const BENEFITS = [
  "Custom G Club Team Jersey — Name & Membership #",
  "All SHYFT Apps FREE FOR LIFE",
  "All SHYFT Indicators FREE FOR LIFE",
  "50% Discount to all SHYFT Events",
  "Access to EazyBot Promotional Rotator",
  "G Club Exclusive Merchandise",
  "G Club Community Group & Badge",
  "First Access to New Products",
];

const INITIAL_DELAY_MS = 4_000; // first surface only — no re-nudges
const DISMISS_KEY = "gclub_modal_dismissed_this_session";
const OPEN_COUNT_KEY = "gclub_modal_open_count"; // 1 = it has fired on this session
const MUTED_KEY = "gclub_modal_muted_this_session";

const track = (event, extra = {}) => {
  try {
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture(event, extra);
    }
  } catch {
    /* silent */
  }
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (base) {
      fetch(`${base}/api/share/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel: event }),
      }).catch(() => {});
    }
  } catch {
    /* silent */
  }
};

export const GenesisClubModal = ({ open, onClose, onOpen }) => {
  const { status } = useGClubStatus();
  const countdown = useCountdown(status?.deadline_iso);

  // Escape + scroll-lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    track("gclub_popup_shown", { remaining: status?.remaining });
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, status?.remaining]);

  const timeUp = !!status && countdown.expired;
  if (!open || !status || status.closed || timeUp) return null;

  const handleClose = (reason = "manual") => {
    track("gclub_popup_dismissed", { reason, remaining: status.remaining });
    try {
      if (reason === "later") {
        sessionStorage.setItem(MUTED_KEY, "1");
      } else {
        sessionStorage.setItem(DISMISS_KEY, "1");
      }
    } catch {
      /* silent */
    }
    onClose(reason);
  };

  const handleCta = () => {
    track("gclub_cta_clicked", { remaining: status.remaining });
    try {
      sessionStorage.setItem(MUTED_KEY, "1");
    } catch {
      /* silent */
    }
    onClose("cta");
  };

  return (
    <div
      data-testid="gclub-modal"
      className="fixed inset-0 z-[110] overflow-y-auto flex items-start md:items-center justify-center px-3 py-4 md:px-4 md:py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gclub-modal-title"
    >
      <button
        type="button"
        aria-label="Close Genesis Club dialog"
        onClick={() => handleClose("backdrop")}
        data-testid="gclub-modal-backdrop"
        className="fixed inset-0 bg-black/85 backdrop-blur-md cursor-default"
      />

      <div className="relative w-full max-w-5xl my-auto rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden border-2 border-[rgba(255,208,102,0.7)] shadow-[0_50px_140px_-20px_rgba(0,0,0,0.95),0_0_100px_-10px_rgba(255,208,102,0.55),0_0_60px_-10px_rgba(56,166,255,0.4)] gclub-modal-pulse">
        {/* Ambient glow layers — brighter, punchier */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-[#1f1a12] via-[#14110e] to-[#050810]"
        />
        <div
          aria-hidden
          className="absolute -top-52 -left-52 w-[760px] h-[760px] rounded-full opacity-80 gclub-glow-a"
          style={{
            background:
              "radial-gradient(circle, rgba(255,208,102,0.55), transparent 62%)",
          }}
        />
        <div
          aria-hidden
          className="absolute -bottom-52 -right-52 w-[760px] h-[760px] rounded-full opacity-80 gclub-glow-b"
          style={{
            background:
              "radial-gradient(circle, rgba(56,166,255,0.6), transparent 62%)",
          }}
        />

        {/* Close — larger hit target on mobile, always reachable */}
        <button
          type="button"
          onClick={() => handleClose("x-button")}
          data-testid="gclub-modal-close"
          aria-label="Close"
          className="absolute z-30 top-3 right-3 md:top-4 md:right-4 inline-flex items-center justify-center w-11 h-11 md:w-9 md:h-9 rounded-full border border-[rgba(255,208,102,0.7)] text-[#f2e6c3] hover:text-white hover:border-[rgba(255,208,102,0.95)] transition bg-[#0a0a0a]/85 backdrop-blur shadow-[0_4px_16px_rgba(0,0,0,0.5)]"
        >
          <X size={18} strokeWidth={2.4} />
        </button>

        <div className="relative grid md:grid-cols-12 gap-0 items-stretch">
          {/* Package image — bigger, brighter halo */}
          <div className="md:col-span-6 relative min-h-[220px] md:min-h-[620px] flex items-center justify-center overflow-hidden p-4 md:p-6">
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(60% 60% at 50% 50%, rgba(255,208,102,0.42), rgba(56,166,255,0.16) 55%, transparent 78%)",
              }}
            />
            <img
              src="/g-club-package.png"
              alt="SHYFT Genesis Club Lifetime Membership package"
              data-testid="gclub-modal-image"
              className="relative w-full h-auto max-h-[220px] md:max-h-[580px] object-contain gclub-image-pulse drop-shadow-[0_0_45px_rgba(255,208,102,0.5)]"
            />
          </div>

          {/* Copy */}
          <div className="md:col-span-6 relative p-5 md:p-10 lg:p-12 flex flex-col justify-center">
            <h2
              id="gclub-modal-title"
              className="font-serif-display mt-4 text-2xl md:text-[2.15rem] lg:text-[2.4rem] leading-[1.05] text-white"
            >
              SHYFT Genesis Club
              <span
                className="block"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, #ffe08a 0%, #ffcc4d 50%, #e7c98a 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Lifetime Membership
              </span>
            </h2>

            <p className="mt-3 text-[#e3ddce] text-[13px] md:text-sm leading-relaxed max-w-xl">
              Membership closes at midnight 9-16-26. Once time runs out, your
              chance to join the Genesis Club closes for good &mdash; no
              exceptions, no waitlist.
            </p>

            {/* Live countdown */}
            <div
              data-testid="gclub-modal-countdown"
              className="mt-4 inline-flex self-start items-center gap-3 px-4 py-3 rounded-xl border border-[rgba(255,208,102,0.35)] bg-[#0a0a0a]/60 backdrop-blur"
              aria-label={`Closes in ${countdown.dd} days ${countdown.hh} hours ${countdown.mm} minutes ${countdown.ss} seconds`}
            >
              <span className="eyebrow !text-[9px] !text-[#ffcc4d]">
                Closes In
              </span>
              <div className="flex items-baseline gap-1 tabular-nums font-serif-display text-white text-xl md:text-2xl">
                <span>{countdown.dd}</span>
                <span className="text-[#ffcc4d] text-sm">d</span>
                <span className="ml-1">{countdown.hh}</span>
                <span className="text-[#ffcc4d] text-sm">h</span>
                <span className="ml-1">{countdown.mm}</span>
                <span className="text-[#ffcc4d] text-sm">m</span>
                <span className="ml-1">{countdown.ss}</span>
                <span className="text-[#ffcc4d] text-sm">s</span>
              </div>
            </div>

            {/* Benefits */}
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-[#f2ecdd] text-[12.5px] leading-snug">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check
                    size={14}
                    strokeWidth={3.2}
                    className="text-[#ffcc4d] mt-0.5 shrink-0 drop-shadow-[0_0_6px_rgba(255,204,77,0.7)]"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {/* Price */}
            <div className="mt-5 flex items-baseline gap-3">
              <span
                className="font-serif-display text-4xl md:text-[2.75rem]"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, #fff2c2 0%, #ffcc4d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                $500
              </span>
              <span className="eyebrow !text-[10px] !text-[#ffcc4d]">
                One-Time · Lifetime · Never Again
              </span>
            </div>

            {/* CTAs */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href={status.product_url}
                target="_blank"
                rel="noreferrer"
                onClick={handleCta}
                data-testid="gclub-modal-cta"
                className="btn-gold gclub-cta-pulse !bg-gradient-to-r !from-[#ffe08a] !via-[#ffcc4d] !to-[#c9a25b]"
              >
                Claim My Membership
                <ArrowUpRight size={18} strokeWidth={2.4} />
              </a>
              <button
                type="button"
                onClick={() => handleClose("later")}
                data-testid="gclub-modal-later"
                className="text-[#cfc8b9] hover:text-white text-[13px] tracking-wide underline underline-offset-4 decoration-[rgba(255,208,102,0.35)] hover:decoration-[rgba(255,208,102,0.85)] transition"
              >
                I&rsquo;ll pass for now
              </button>
            </div>

            <div className="vault-divider mt-6" />
            <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-[#a89b7f]">
              First 50 only · No waitlist · No exceptions
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Controller — opens the modal exactly ONCE per browser tab session,
 *  4 seconds after page load. After that, only the ribbon or chip can
 *  bring it back (user-initiated). No re-nudges, no exit-intent, no
 *  nav-click trigger. */
export const useGenesisClubController = () => {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const isMuted = () => {
    try {
      return !!sessionStorage.getItem(MUTED_KEY);
    } catch {
      return false;
    }
  };

  const hasFiredThisSession = () => {
    try {
      return !!sessionStorage.getItem(OPEN_COUNT_KEY);
    } catch {
      return false;
    }
  };

  const markFired = () => {
    try {
      sessionStorage.setItem(OPEN_COUNT_KEY, "1");
    } catch {
      /* silent */
    }
  };

  const forceOpen = () => {
    // User-initiated (ribbon or chip). Always respected.
    clearTimer();
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isMuted() || hasFiredThisSession()) return;

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (isMuted() || hasFiredThisSession()) return;
      markFired();
      setOpen(true);
    }, INITIAL_DELAY_MS);

    return () => {
      clearTimer();
    };
  }, []);

  return { open, close, forceOpen };
};

export default GenesisClubModal;
